/**
 * "Search what was said" in the Gallery and Strips looks: every word in every video, with the
 * results as clips (picture, time, the line) grouped by video. A result opens the quick look at
 * that line. The library's other search box only matches video names; this one is the words.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiRequest from "../../../../utils/apiClient";
import { hostApi } from "../../../constants";
import { ROUTES } from "../../../../constants";
import Pic from "./Pic";
import { SAID } from "./libraryLookText";
import { clock } from "./topClips";

interface Result {
  sentence_id: number;
  sentence: string;
  start_in: number;
  thumbnail: string;
  transcript_id: number;
  transcript_title: string;
}

const CAP = 200;

const highlight = (text: string, q: string): React.ReactNode => {
  const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return text;
  return <>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
};

const SaidSearch: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const asked = params.get("q") || "";
  const [text, setText] = useState(asked);
  const [results, setResults] = useState<Result[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => { input.current?.focus(); }, []);

  useEffect(() => {
    setText(asked);
    if (asked.trim().length < 2) { setResults(null); return undefined; }
    let cancelled = false;
    setBusy(true);
    setFailed(false);
    apiRequest(`${hostApi}cross-video-search/?q=${encodeURIComponent(asked.trim())}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const body = await res.json();
        if (!cancelled) setResults(body.results || []);
      })
      .catch(() => { if (!cancelled) { setFailed(true); setResults([]); } })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [asked]);

  const groups: { id: number; title: string; rows: Result[] }[] = [];
  (results || []).forEach((r) => {
    let g = groups.find((x) => x.id === r.transcript_id);
    if (!g) { g = { id: r.transcript_id, title: r.transcript_title, rows: [] }; groups.push(g); }
    g.rows.push(r);
  });
  const look = (id: number, at?: number) =>
    navigate(`${ROUTES.MY_LIBRARY_UPLOADS}?v=${id}${typeof at === "number" ? `&at=${Math.floor(at)}` : ""}`);

  return (
    <div>
      <div className="ttl" style={{ fontSize: 24, marginTop: 14 }}>{SAID.title}</div>
      <div className="hint">{SAID.sub}</div>
      <form className="bigfind" onSubmit={(e) => { e.preventDefault(); setParams(text.trim() ? { q: text.trim() } : {}); }}>
        <span aria-hidden>💬</span>
        <input ref={input} type="search" value={text} placeholder={SAID.placeholder} onChange={(e) => setText(e.target.value)} />
        <button type="submit" className="pill red">↵</button>
      </form>
      {busy && <div className="meta">{SAID.searching}</div>}
      {failed && <div className="meta">{SAID.error}</div>}
      {!busy && results && results.length === 0 && !failed && <div className="meta">{SAID.none(asked)}</div>}
      {!busy && results && results.length > 0 && (
        <div className="meta" style={{ marginBottom: 14 }}>
          {SAID.count(results.length, groups.length)}{results.length >= CAP ? ` ${SAID.capped}` : ""}
        </div>
      )}
      {!busy && groups.map((g) => (
        <div key={g.id} className="said-group">
          <div className="hd">
            <div className="ttl">{g.title}</div>
            <button type="button" className="pill line" onClick={() => look(g.id)}>{SAID.openVideo}</button>
          </div>
          <div className="qgrid" style={{ marginTop: 0 }}>
            {g.rows.map((r) => (
              <div key={r.sentence_id} className="mom" onClick={() => look(g.id, r.start_in)} title={r.sentence}>
                <Pic src={r.thumbnail}><span className="t">{clock(r.start_in)}</span></Pic>
                <p>“{highlight(r.sentence, asked)}”</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SaidSearch;
